/**
 * Free printable worksheets for the Parents & Teachers page. Each one has its own page at
 * /worksheets/<slug> (indexable, so `intro` + `teacherNotes` must stay above 150 words) and, if it
 * has right answers, an answer key at /worksheets/<slug>/answers (noindex).
 *
 * `kind` picks the sheet layout in components/worksheets/WorksheetSheet.js. Answers for the
 * element and reaction sheets are worked out from the lab's own data, so they always match the lab.
 */
export const WORKSHEET_KINDS = Object.freeze({
  SPOT_THE_ELEMENT: 'spot-the-element',
  FIZZ_OR_NO_FIZZ: 'fizz-or-no-fizz',
  METALS_SORT: 'metals-sort',
  LAB_NOTEBOOK: 'lab-notebook',
  ALKALI_METALS: 'alkali-metals',
});

export const WORKSHEETS = Object.freeze([
  {
    slug: 'spot-the-element',
    kind: WORKSHEET_KINDS.SPOT_THE_ELEMENT,
    title: 'Spot the Element',
    summary:
      'Free printable worksheet: match 16 element symbols and names, including the tricky ones from Latin like Fe, Na and Au. With answer key.',
    ages: '7 to 10',
    minutes: 15,
    intro: [
      'Every element has a short symbol of one or two letters. Scientists all over the world use the same symbols, so a chemist in Japan and a chemist in Brazil can read the same equation.',
      'In Part A, write the full name of each element next to its symbol. In Part B, do the opposite: write the symbol next to each name. Watch out! Some symbols come from old Latin names, so they do not match the English name at all.',
    ],
    teacherNotes: [
      'Part A uses symbols that start with the same letter as the element, so most children can work them out. Part B includes eight elements whose symbols come from Latin or German names, such as Fe for iron (ferrum) and K for potassium (kalium). These are the ones children usually get wrong, and they make a good talking point.',
      'Children can check their answers on the periodic table in the ChemistFella lab by tapping each tile. As an extension, ask them to find three more elements whose symbols do not match their English names, and to look up where the symbol comes from on each element page.',
    ],
    symbolsToName: ['H', 'O', 'C', 'N', 'He', 'Ca', 'Cl', 'Ne'],
    namesToSymbol: ['Fe', 'Na', 'K', 'Au', 'Ag', 'Pb', 'Cu', 'Sn'],
    hasAnswers: true,
  },
  {
    slug: 'fizz-or-no-fizz',
    kind: WORKSHEET_KINDS.FIZZ_OR_NO_FIZZ,
    title: 'Fizz or No Fizz?',
    summary:
      'Free printable worksheet: predict which metals fizz with water or acid, then test each pair in the ChemistFella virtual lab. With answer key.',
    ages: '8 to 12',
    minutes: 25,
    intro: [
      'Some metals fizz and bubble when they meet water or acid, because they make hydrogen gas. Others just sit there and nothing happens at all. Can you predict which is which?',
      'For each pair, first tick Fizz or No fizz to show your prediction. Then mix the pair in the ChemistFella lab and write down what really happened. Were you right? At the end, count how many predictions you got correct.',
    ],
    teacherNotes: [
      'This sheet introduces the idea that metals have different reactivity. The very reactive metals (potassium, calcium) react with water, magnesium, zinc and iron react with hydrochloric acid, and the least reactive metals (copper, silver, gold) do not react with either. Helium is included as a reminder that noble gases do not react.',
      'Encourage children to give a reason for each prediction, even a guess such as "gold never goes rusty". After testing, ask them to sort the metals from most to least reactive. This is a simple version of the reactivity series taught in secondary school. All the reactions are virtual, so children must never try them with real chemicals.',
    ],
    pairs: [
      ['K', 'H2O'],
      ['Au', 'H2O'],
      ['Mg', 'HCl'],
      ['Cu', 'HCl'],
      ['Ca', 'H2O'],
      ['He', 'O'],
      ['Zn', 'HCl'],
      ['Ag', 'H2O'],
      ['Fe', 'HCl'],
      ['Ag', 'HCl'],
    ],
    hasAnswers: true,
  },
  {
    slug: 'metals-and-non-metals',
    kind: WORKSHEET_KINDS.METALS_SORT,
    title: 'Metals and Non-metals Sort',
    summary:
      'Free printable worksheet: sort 16 elements into metals, non-metals and metalloids, then explain your choices. Includes an answer key for teachers.',
    ages: '8 to 12',
    minutes: 20,
    intro: [
      'Most elements are metals. Metals are usually shiny, they let electricity and heat pass through them, and they can be bent or hammered into shape. Non-metals are often gases, or dull and brittle solids, and most do not carry electricity. A few elements, called metalloids, are a bit of both.',
      'Write each element from the box into the right column. Use the periodic table in the lab to help you. Then answer the questions at the bottom.',
    ],
    teacherNotes: [
      'On the periodic table, metals are on the left and in the middle, non-metals are on the right, and the metalloids form a staircase between them. Boron and silicon are the two metalloids on this sheet. Hydrogen is left out on purpose, because it sits on the left of the table but is a non-metal, which confuses many children.',
      'A good discussion question is why mercury is a metal even though it is a liquid, or why carbon is a non-metal even though graphite carries electricity. These show that scientists group elements by several properties at once, not just one. The lab colours each family differently, so children can check their answers by looking at the tile colours.',
    ],
    symbols: ['Fe', 'O', 'Cu', 'S', 'Na', 'He', 'Al', 'C', 'Si', 'Au', 'Cl', 'Mg', 'N', 'B', 'Zn', 'Ne'],
    questions: [
      { question: 'Name one property that most metals share.', answer: 'They are shiny, conduct electricity and heat, and can be bent or hammered into shape.' },
      { question: 'Which side of the periodic table are most non-metals on?', answer: 'The right-hand side.' },
      { question: 'Which two elements on this sheet are metalloids?', answer: 'Boron (B) and silicon (Si).' },
    ],
    hasAnswers: true,
  },
  {
    slug: 'my-lab-notebook',
    kind: WORKSHEET_KINDS.LAB_NOTEBOOK,
    title: 'My Lab Notebook',
    summary:
      'Free printable lab notebook page for kids: record a prediction, what happened and why for three experiments in the ChemistFella virtual lab.',
    ages: '7 to 12',
    minutes: 20,
    intro: [
      'Real scientists write everything down in a lab notebook. They write what they plan to do, what they think will happen, what really happened and why. That way they can remember their results and share them with other scientists.',
      'Choose two substances in the ChemistFella lab. Before you press Mix, write your prediction. Then press Mix, watch carefully and write what happened. Listen to Fizzy explain why, and write the reason in your own words. Do this for three different experiments.',
    ],
    teacherNotes: [
      'This open-ended sheet works with any pair in the lab, so children can follow their own curiosity. If they need ideas, suggest the famous reactions list, or the pairs printed at the bottom of the sheet. Encourage them to write the safety note too, because many of the reactions would be dangerous in real life.',
      'The most important skill here is separating a prediction from an observation. Children often change their prediction after seeing the result, so ask them to write it in pen before pressing Mix. The sheet has no answer key, because every child will choose different experiments. You could finish by asking each child to share their most surprising result with the class.',
    ],
    entryCount: 3,
    suggestedPairs: [
      ['Na', 'Cl'],
      ['Fe', 'S'],
      ['Cu', 'Zn'],
      ['H', 'O'],
      ['He', 'F'],
      ['C', 'O'],
    ],
    hasAnswers: false,
  },
  {
    slug: 'alkali-metals-in-water',
    kind: WORKSHEET_KINDS.ALKALI_METALS,
    title: 'Alkali Metals in Water',
    summary:
      'Free printable worksheet: compare lithium, sodium and potassium in water in the virtual lab and spot the pattern down the group. With answer key.',
    ages: '9 to 12',
    minutes: 30,
    intro: [
      'Lithium, sodium and potassium are all alkali metals. They sit in the first column of the periodic table, one above the other. They all react with water, but not in the same way.',
      'Mix each metal with water in the ChemistFella lab. Watch closely and listen to Fizzy. Fill in the table for each metal, then answer the questions. Can you spot a pattern as you go down the column?',
    ],
    teacherNotes: [
      'This is a classic demonstration in secondary school science, but it is far too dangerous for children to do with real metals. The virtual lab lets them see the pattern safely: going down the group, each metal reacts more violently than the one above. Lithium fizzes steadily, sodium melts into a ball and zooms around, and potassium bursts into a lilac flame.',
      'The reason is that each atom has one outer electron, and in bigger atoms it is further from the centre and easier to give away. Older children can use the last question to predict how rubidium and caesium would behave. The reaction pages for each pair have longer explanations and quizzes if children finish early.',
    ],
    metals: ['Li', 'Na', 'K'],
    tableRows: [
      { label: 'Does it float?', answers: { Li: 'Yes', Na: 'Yes', K: 'Yes' } },
      { label: 'Does it melt into a ball?', answers: { Li: 'No', Na: 'Yes', K: 'Yes' } },
      { label: 'Does it catch fire? What colour?', answers: { Li: 'Usually not', Na: 'Sometimes, yellow-orange', K: 'Yes, lilac' } },
      { label: 'How fast is the reaction?', answers: { Li: 'Slow and steady', Na: 'Fast', K: 'Very fast and violent' } },
    ],
    questions: [
      { question: 'Which metal reacted most violently?', answer: 'Potassium.' },
      { question: 'What gas makes the bubbles in all three reactions?', answer: 'Hydrogen.' },
      { question: 'What has the water turned into by the end?', answer: 'An alkali, a metal hydroxide such as sodium hydroxide.' },
      { question: 'Rubidium is below potassium. Predict how it would react with water.', answer: 'Even more violently than potassium, because its outer electron is given away even more easily.' },
    ],
    hasAnswers: true,
  },
]);

export function findWorksheetBySlug(slug) {
  return WORKSHEETS.find((worksheet) => worksheet.slug === slug) ?? null;
}
